import React, { useEffect, useState } from 'react';
import {
  Typography,
  Sizes,
  Rank,
  Heading,
  AlertBanner,
  getRehydratableName,
  TextBoldness,
  getClassNameFactory,
} from '@dcl/ui';
import { types, boldTypes } from '@dcl/ui/dist/elements/Heading';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { IChatWithUsProps } from './ChatWithUs.props';
import { Cross } from '@dcl/icons/lib/ui';
import ChatWithUsIcon from './ChatWithUsIcon';
import './index.css';
import { AlertBannerType } from '@dcl/ui/dist/fragments/AlertBanner/AlertBannerConstants';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import { CHAT_ALERT_TIMEOUT, OFFLINE_ALERT_TEXT } from './constants';

const ChatWithUs: React.FC<IChatWithUsProps> = (props) => {
  const { title, description, buttonText, isBorderVisible = false } = props;
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertTimer, setAlertTimer] = useState(0);

  useEffect(() => {
    return () => {
      clearTimeout(alertTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAlertClose = () => {
    clearTimeout(alertTimer);
    setIsAlertOpen(false);
  };

  const handleChatNowBtnClick = () => {
    // if user clicks on Chat Now button then open the chatbox by triggering a click on it.
    const chatBtn: any = document.querySelector(
      '.embeddedServiceHelpButton button'
    );
    if (chatBtn?.classList.contains('helpButtonEnabled')) {
      chatBtn.click();
    } else if (!isAlertOpen) {
      setIsAlertOpen(true);
      setAlertTimer(
        Number(
          setTimeout(() => {
            setIsAlertOpen(false);
          }, CHAT_ALERT_TIMEOUT)
        )
      );
    }
  };

  const getClassName = getClassNameFactory(ChatWithUs.displayName);
  return (
    <>
      <div
        className={getClassName()}
        data-rehydratable={getRehydratableName(ChatWithUs.displayName)}
        {...propsToDataAttributes(props)}
      >
        <div
          className={getClassName({
            descendantName: 'container',
            modifiers: isBorderVisible && 'border',
          })}
          data-testid="ChatWithUs-border"
        >
          <div
            className={getClassName({
              descendantName: 'textContainer',
              modifiers: isBorderVisible && 'visible',
            })}
          >
            <Heading
              type={types.XtraSmall}
              boldType={boldTypes.black}
              rank={Rank.Second}
              className={getClassName({ descendantName: 'heading' })}
              data-testid="ChatWithUs-heading"
            >
              {title}
            </Heading>
            <Typography
              type={Sizes.XXTRASMALL}
              tag={TypographyTags.P}
              className={getClassName({ descendantName: 'description' })}
              data-testid="ChatWithUs-description"
            >
              {description}
            </Typography>
          </div>
          <div className={getClassName({ descendantName: 'buttonContainer' })}>
            <button
              onClick={handleChatNowBtnClick}
              className={getClassName({ descendantName: 'button' })}
              data-testid="ChatWithUs-button"
            >
              {ChatWithUsIcon}
              <Typography
                type={Sizes.XXTRASMALL}
                tag={TypographyTags.P}
                boldness={TextBoldness.Medium}
                className={getClassName({ descendantName: 'buttonText' })}
              >
                {buttonText}
              </Typography>
            </button>
          </div>
        </div>
      </div>
      <div className={getClassName({ descendantName: 'alertContainer' })}>
        {isAlertOpen && (
          <AlertBanner bannerType={AlertBannerType.WARNING}>
            {OFFLINE_ALERT_TEXT}
            <button
              onClick={handleAlertClose}
              className={getClassName({ descendantName: 'alertCloseBtn' })}
              data-testid="chatAlert-closeBtn"
            >
              <Cross />
            </button>
          </AlertBanner>
        )}
      </div>
    </>
  );
};

ChatWithUs.displayName = 'ChatWithUs';

export default ChatWithUs;
